"use client";

import CtaArrow from "@/components/svg/CtaArrow";
import SubHeader from "@/components/SubHeader";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { getHightlightedTextComponents } from "@/helpers";
import { BRIEF_FIELDS } from "@/lib/brief-fields";
import { useState } from "react";

const fieldBoxClassName =
  "flex flex-col justify-center gap-2 rounded-lg border border-bleu/80 bg-blanc px-4 py-3 relative";

const fieldLabelClassName = "text-sm leading-4 text-bleu";

const fieldInputClassName =
  "w-full border-0 bg-transparent p-0 text-base leading-5 text-bleu-50 placeholder:text-bleu-50/70 focus:outline-none";

const PHONE_COUNTRY_OPTIONS = [
  { code: "+33", label: "🇫🇷 +33" },
];

const ChevronDown = ({ className = "text-bleu" }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className={`shrink-0 ${className}`}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * @param {{
 *   number: number;
 *   title?: import("@prismicio/client").RichTextField;
 *   text?: import("@prismicio/client").RichTextField;
 *   fallbackTitle: import("react").ReactNode;
 *   fallbackText: string;
 * }} props
 */
const FormSectionHeader = ({
  number,
  title,
  text,
}) => (
  <div className="flex gap-4 items-center">
    <div
      className="hidden md:flex items-center justify-center rounded-[10px] bg-vert-50 w-[48px] h-[48px]"
      aria-hidden
    >
      <span className="text-[36px] font-extralight leading-none tracking-[0.5px] text-blanc">
        {number}
      </span>
    </div>

    <div className="flex flex-col gap-1">
      {isFilled.richText(title) && (
        <div className="flex flex-row items-center md:hidden gap-2">
          <div
            className="flex md:hidden items-center justify-center rounded-[2px] bg-vert-50 w-[22px] h-[22px] mt-1"
            aria-hidden>
            <div className="cta-text font-extralight leading-none text-blanc">
              {number}
            </div>
          </div>
          <PrismicRichText field={title} components={getHightlightedTextComponents({ highlightColor: "beige" })} />
        </div>
      )}

      {isFilled.richText(text) && (
        <PrismicRichText
          field={text}
          components={{
            paragraph: ({ children }) => (
              <p className="p-sm text-bleu-50">{children}</p>
            ),
          }}
        />
      )}
    </div>
  </div>
);

const normalizeSelectOption = (option) =>
  typeof option === "string" ? { label: option, value: option } : option;

/**
 * @param {{
 *   id: string;
 *   label: string;
 *   value: string;
 *   onChange: (value: string) => void;
 *   options: Array<string | { label: string; value: string }>;
 *   required?: boolean;
 * }} props
 */
const FormSelectField = ({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
}) => (
  <div className={fieldBoxClassName}>
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className={`${fieldLabelClassName} pr-4 md:pr-0`}>
        {label}
      </label>
      <select
        id={id}
        name={id}
        required={required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${fieldInputClassName} appearance-none pr-8`}
      >
        <option value="" disabled>
          Sélectionner
        </option>
        {options.map((option) => {
          const { label: optionLabel, value: optionValue } =
            normalizeSelectOption(option);
          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </div>
    <ChevronDown className="pointer-events-none absolute right-2 text-bleu" />
  </div>
);

/**
 * @param {{
 *   id: string;
 *   label: string;
 *   type?: string;
 *   value: string;
 *   onChange: (value: string) => void;
 *   placeholder?: string;
 *   autoComplete?: string;
 *   required?: boolean;
 * }} props
 */
const FormTextField = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  required = false,
}) => (
  <div className={fieldBoxClassName}>
    <label htmlFor={id} className={fieldLabelClassName}>
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className={fieldInputClassName}
    />
  </div>
);

/**
 * @param {{
 *   countryCode: string;
 *   phone: string;
 *   onCountryCodeChange: (value: string) => void;
 *   onPhoneChange: (value: string) => void;
 * }} props
 */
const FormPhoneField = ({
  countryCode,
  phone,
  onCountryCodeChange,
  onPhoneChange,
}) => (
  <div className="flex gap-2.5">
    <div className={`${fieldBoxClassName} w-[127px] shrink-0 justify-center`}>
      <span className="sr-only">Indicatif téléphonique</span>
      <div className="relative flex items-center">
        <select
          id="phoneCountry"
          name="phoneCountry"
          value={countryCode}
          onChange={(event) => onCountryCodeChange(event.target.value)}
          className={`${fieldInputClassName} appearance-none pr-6`}
          aria-label="Indicatif téléphonique"
        >
          {PHONE_COUNTRY_OPTIONS.map(({ code, label }) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-0 size-4 text-bleu" />
      </div>
    </div>

    <div className={`${fieldBoxClassName} min-w-0 flex-1`}>
      <label htmlFor="phone" className={fieldLabelClassName}>
        Téléphone
      </label>
      <input
        id="phone"
        name="phone"
        type="tel"
        value={phone}
        onChange={(event) => onPhoneChange(event.target.value)}
        placeholder="06 77 39 31 33"
        autoComplete="tel-national"
        className={fieldInputClassName}
      />
    </div>
  </div>
);

const initialFormState = {
  projectType: "",
  mainObjective: "",
  existing: "",
  advancement: "",
  budget: "",
  deadline: "",
  firstName: "",
  lastName: "",
  company: "",
  contactPreference: "",
  email: "",
  phoneCountry: "+33",
  phone: "",
};

/**
 * @typedef {import("@prismicio/client").Content.FormSlice} FormSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FormSlice>} FormProps
 * @type {import("react").FC<FormProps>}
 */
const Form = ({ slice }) => {
  const { form_id, sub_header, title, group } = slice.primary;
  const [form, setForm] = useState(initialFormState);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const sections = [0, 1, 2].map((index) => group?.[index] ?? null);

  const updateField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (status !== "idle") {
      setStatus("idle");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("submitting");

    const phone = form.phone
      ? `${form.phoneCountry} ${form.phone}`.trim()
      : "";

    const response = await fetch("/api/contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone,
        company: form.company,
        projectType: form.projectType,
        mainObjective: form.mainObjective,
        existing: form.existing,
        advancement: form.advancement,
        budget: form.budget,
        deadline: form.deadline,
        contactPreference: form.contactPreference,
        formId: form_id || "",
      }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      setStatus("error");
      setErrorMessage(
        (data && data.error) ||
        "Une erreur est survenue lors de l'envoi du message.",
      );
      return;
    }

    setForm(initialFormState);
    setStatus("success");
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y"
    >
      <div className="framing flex flex-col items-left gap-10 md:gap-22">
        <div className="flex w-full flex-col gap-3 md:max-w-[560px] md:shrink-0">
          {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}

          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => (
                  <h2 className="p-xl-n text-bleu">{children}</h2>
                ),
                heading2: ({ children }) => (
                  <h2 className="p-xl-n text-bleu">{children}</h2>
                ),
              }}
            />
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col gap-15"
          noValidate={false}
        >
          <div className="flex flex-col gap-15">
            <div className="flex flex-col gap-7">
              <FormSectionHeader
                number={1}
                title={sections[0]?.title}
                text={sections[0]?.text}
              />

              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
                <FormSelectField
                  id="projectType"
                  label={BRIEF_FIELDS.projectType.question}
                  value={form.projectType}
                  onChange={updateField("projectType")}
                  options={BRIEF_FIELDS.projectType.options}
                  required
                />
                <FormSelectField
                  id="mainObjective"
                  label={BRIEF_FIELDS.mainObjective.question}
                  value={form.mainObjective}
                  onChange={updateField("mainObjective")}
                  options={BRIEF_FIELDS.mainObjective.options}
                  required
                />
                <FormSelectField
                  id="existing"
                  label={BRIEF_FIELDS.existing.question}
                  value={form.existing}
                  onChange={updateField("existing")}
                  options={BRIEF_FIELDS.existing.options}
                  required
                />
                <FormSelectField
                  id="advancement"
                  label={BRIEF_FIELDS.advancement.question}
                  value={form.advancement}
                  onChange={updateField("advancement")}
                  options={BRIEF_FIELDS.advancement.options}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-7">
              <FormSectionHeader
                number={2}
                title={sections[1]?.title}
                text={sections[1]?.text}
              />

              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
                <FormSelectField
                  id="budget"
                  label={BRIEF_FIELDS.budget.question}
                  value={form.budget}
                  onChange={updateField("budget")}
                  options={BRIEF_FIELDS.budget.options}
                  required
                />
                <FormSelectField
                  id="deadline"
                  label={BRIEF_FIELDS.deadline.question}
                  value={form.deadline}
                  onChange={updateField("deadline")}
                  options={BRIEF_FIELDS.deadline.options}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-7">
              <FormSectionHeader
                number={3}
                title={sections[2]?.title}
                text={sections[2]?.text}
              />

              <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
                <FormTextField
                  id="firstName"
                  label="Prénom"
                  value={form.firstName}
                  onChange={updateField("firstName")}
                  placeholder="Votre prénom"
                  autoComplete="given-name"
                  required
                />
                <FormTextField
                  id="lastName"
                  label="Nom"
                  value={form.lastName}
                  onChange={updateField("lastName")}
                  placeholder="Votre nom"
                  autoComplete="family-name"
                  required
                />
                <FormTextField
                  id="company"
                  label="Entreprise"
                  value={form.company}
                  onChange={updateField("company")}
                  placeholder="Votre entreprise"
                  autoComplete="organization"
                />
                <FormSelectField
                  id="contactPreference"
                  label={BRIEF_FIELDS.contactPreference.question}
                  value={form.contactPreference}
                  onChange={updateField("contactPreference")}
                  options={BRIEF_FIELDS.contactPreference.options}
                  required
                />
                <FormTextField
                  id="email"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={updateField("email")}
                  placeholder="Votre email"
                  autoComplete="email"
                  required
                />
                <FormPhoneField
                  countryCode={form.phoneCountry}
                  phone={form.phone}
                  onCountryCodeChange={updateField("phoneCountry")}
                  onPhoneChange={updateField("phone")}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="cta bg-rouge text-blanc cta-text disabled:cursor-not-allowed disabled:opacity-70"
            >
              {status === "submitting" ? "Envoi en cours…" : "envoyer ma demande"}
              <CtaArrow className="text-blanc" />
            </button>

            {status === "success" && (
              <p className="p-sm text-vert" role="status">
                Votre demande a bien été envoyée. Nous vous recontacterons rapidement.
              </p>
            )}

            {status === "error" && (
              <p className="p-sm text-rouge" role="alert">
                {errorMessage}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default Form;
